import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ChevronDown, Coins, ExternalLink, LogOut, User } from 'lucide-react'

import { USER_MENU_GROUPS, isNavItemVisible, isUserMenuActive } from '@/constants/user-menu'
import { PageIconBadge } from '@/shared/components/PageIconBadge'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { creatorPublicPath, resolveUserDisplayName, userPublicHandle } from '@/utils/userDisplay'
import { useAuth, usePoints, useTenantFeatures } from '@/hooks'
import { cn } from '@/shared/lib/utils'

type UserMenuProps = {
  /** 对齐 studio-web portal-user-chip（仅 xl 可见） */
  variant?: 'default' | 'portal'
}

export function UserMenu({ variant = 'default' }: UserMenuProps) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { user, logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const { balance } = usePoints({ enabled: menuOpen })
  const { isEnabled } = useTenantFeatures()

  const displayName = user ? resolveUserDisplayName(user) : ''
  const handle = user ? userPublicHandle(user) : null
  const initial = displayName.charAt(0).toUpperCase() || '?'

  const menuGroups = USER_MENU_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => isNavItemVisible(item, isEnabled)),
  })).filter((group) => group.items.length > 0)

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={clsx(
            'h-auto gap-2 px-2.5 py-1.5 font-medium',
            variant === 'portal'
              ? 'portal-user-chip is-visible'
              : clsx(
                  'rounded-full border text-[13px]',
                  'border-white/[0.08] bg-white/[0.04] text-zinc-200 hover:border-indigo-500/35 hover:bg-white/[0.06] data-[state=open]:border-indigo-500/45 data-[state=open]:bg-indigo-500/10 data-[state=open]:text-zinc-100 data-[state=open]:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
                ),
          )}
        >
          <span
            className={clsx(
              variant === 'portal'
                ? 'portal-user-avatar'
                : 'grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-indigo-500/80 to-purple-500/80 text-xs font-semibold text-white',
            )}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className={variant === 'portal' ? undefined : 'h-7 w-7 rounded-full object-cover'} />
            ) : (
              initial
            )}
          </span>
          <span className={clsx('max-w-[5rem] truncate', variant === 'portal' ? 'hidden sm:inline' : 'hidden sm:inline')}>
            {displayName}
          </span>
          {variant !== 'portal' && (
            <ChevronDown size={14} className="hidden shrink-0 text-zinc-400 transition group-data-[state=open]:rotate-180 sm:block" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[15rem] max-w-[24rem]">
        <DropdownMenuLabel className="px-3 py-2.5 font-normal">
          <p className="break-words text-sm font-medium text-zinc-100">{displayName}</p>
          {handle ? (
            <p className="break-all text-xs text-zinc-500">@{handle}</p>
          ) : null}
          <Link
            to="/account/revenue"
            className="mt-2 flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-500/20"
          >
            <Coins size={12} />
            {t('auth.pointsBalance')}: {balance}
          </Link>
          {user?.id ? (
            <Link
              to={creatorPublicPath({ id: user.id })}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-1.5 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-2 py-1 text-xs text-indigo-200 hover:bg-indigo-500/20"
            >
              {t('userMenu.mySpace')}
              <ExternalLink size={11} />
            </Link>
          ) : null}
        </DropdownMenuLabel>

        {menuGroups.map((group, groupIdx) => (
          <DropdownMenuGroup key={group.i18nKey}>
            {groupIdx > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-zinc-500">
              {t(group.i18nKey)}
            </DropdownMenuLabel>
            {group.items.map((item) => {
              const Icon = item.icon
              const active = isUserMenuActive(pathname, item)
              return (
                <DropdownMenuItem key={item.to} asChild>
                  <Link
                    to={item.to}
                    className={cn(
                      'flex items-center gap-2',
                      active && 'bg-indigo-500/15 text-indigo-200 focus:bg-indigo-500/15 focus:text-indigo-200',
                    )}
                  >
                    <PageIconBadge icon={Icon} tone={item.tone} size="sm" />
                    {t(item.i18nKey)}
                  </Link>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuGroup>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onClick={() => void logout()}
          className="cursor-pointer"
        >
          <LogOut size={14} />
          {t('auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** 未登录时顶栏占位(与 UserMenu 尺寸接近) */
export function UserMenuPlaceholder() {
  return (
    <span className="grid h-7 w-7 place-items-center rounded-full bg-muted text-zinc-500">
      <User size={14} />
    </span>
  )
}
